/**
 * Module 00 — Global Shell · Bootstrap + hydrate (FE-00-07)
 *
 * Hydrates the scope store so the shell chrome (org switcher, scope trail,
 * credits, avatar, RBAC nav filtering) reflects the signed-in user.
 *
 * The canonical source is the BFF `GET /api/v1/session/bootstrap` [NEW]. Until
 * that endpoint ships everywhere we degrade gracefully to the data the existing
 * auth context already holds (`useAuth`: user / organization / projects), so
 * the shell is populated today rather than blank. When the BFF call succeeds it
 * takes precedence.
 */

'use client';

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/hooks/useAuth';
import { useScopeStore, getActiveDepartmentId } from '@/lib/stores/scope-store';
import { orgIdFromToken } from '@/lib/shell/onboarding-telemetry';
import {
  sessionService,
  type SessionBootstrap,
  type Role,
} from '@/lib/api/services/session';
import type {
  IdentityUser,
  IdentityOrganization,
  IdentityProject,
} from '@/lib/api/identity';

function initialsFrom(user: IdentityUser | null): string {
  if (!user) return '—';
  const first = user.firstName?.trim();
  const last = user.lastName?.trim();
  if (first || last) return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase() || '—';
  const handle = user.username || user.email || '';
  return handle.slice(0, 2).toUpperCase() || '—';
}

function nameFrom(user: IdentityUser | null): string {
  if (!user) return '—';
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return full || user.username || user.email || '—';
}

function roleFrom(user: IdentityUser | null, orgId?: string): Role {
  const entry = user?.orgs?.find((o) => o.orgId === orgId) ?? user?.orgs?.[0];
  return (entry?.role as Role) ?? 'power_user';
}

/** Build a SessionBootstrap from the existing auth context (fallback). */
function fallbackBootstrap(
  user: IdentityUser | null,
  organization: IdentityOrganization | null,
  project: IdentityProject | null,
  projects: IdentityProject[],
): SessionBootstrap | null {
  if (!user) return null;
  const availableProjects = projects.map((p) => ({ id: p.id, name: p.name }));
  const selectedProject = project
    ? { id: project.id, name: project.name }
    : availableProjects[0] ?? { id: '', name: '—' };

  return {
    user: {
      id: user.id,
      name: nameFrom(user),
      email: user.email,
      initials: initialsFrom(user),
      role: roleFrom(user, organization?.id),
    },
    org: {
      id: organization?.id ?? '',
      name: organization?.name ?? 'Organization',
      // identity org has no plan today; surface a neutral default.
      plan: 'Enterprise',
    },
    scope: {
      // Departments are a NEW scope entity — none exist yet, so leave empty.
      department: { id: '', name: '—' },
      project: selectedProject,
      availableDepartments: [],
      availableProjects,
    },
    // Credits source (token-usage summary + quota) wired in FE-00-09;
    // unknown here → zeroed totals make the meter render a neutral "—".
    credits: { used: 0, total: 0 },
    // No RBAC endpoint yet → empty capabilities means "unfiltered" nav.
    capabilities: [],
  };
}

export function useShellBootstrap() {
  const { user, organization, project, projects, isAuthenticated } = useAuth();
  const hydrate = useScopeStore((s) => s.hydrate);

  // The department this bootstrap is FOR, named explicitly in the request:
  // identityClient attaches no scope headers, so relying on x-department-id
  // meant the server always resolved the DEFAULT department and returned that
  // one's projects, whatever department the shell was showing. It's in the
  // query key too, so a department switch refetches instead of re-serving the
  // previous department's cached payload. Falls back to the persisted
  // selection (read once per mount) because the store hasn't hydrated on the
  // first call — the same precedence getScopeHeaders() uses.
  const storeDeptId = useScopeStore((s) => s.department?.id);
  const [initialDeptId] = useState(() => getActiveDepartmentId());
  const deptId = storeDeptId ?? initialDeptId ?? undefined;

  // Seed the org id from the JWT the moment we're authenticated — independent of
  // the (sometimes slow/flaky) bootstrap + auth-context. EVERY scoped API call
  // (getScopeHeaders → x-organization-id) and every create-project flow reads
  // scope.org.id; without a reliable id a lagging/failed bootstrap leaves the org
  // "not in scope", which surfaces as: listProjects returns nothing (the broken
  // "Create your first project" dashboard state with a real project hidden), and
  // Create Project erroring "No organization in scope". The JWT always carries
  // orgId, so seed a minimal org now; the bootstrap fills in the real name later
  // (merge-preserving hydrate keeps this id).
  useEffect(() => {
    if (!isAuthenticated) return;
    const tokenOrg = orgIdFromToken();
    if (!tokenOrg) return;
    const cur = useScopeStore.getState().org;
    if (!cur?.id) {
      useScopeStore.setState({
        org: { id: tokenOrg, name: cur?.name ?? '—', plan: cur?.plan ?? 'Enterprise' },
      });
    }
  }, [isAuthenticated]);

  const bff = useQuery<SessionBootstrap>({
    queryKey: ['session', 'bootstrap', deptId ?? null],
    queryFn: () => sessionService.bootstrap(deptId ? { deptId } : {}),
    enabled: isAuthenticated,
    staleTime: 60_000,
    // The sidebar chrome (org / DEPARTMENT / project) is hydrated from this call,
    // and department has NO fallback source — so a single transient failure would
    // leave the sidebar blank for the whole session, only "fixed" by a manual
    // refresh. Self-heal instead: retry network/5xx/401 (e.g. the first call
    // racing the auth token onto the axios interceptor) with backoff, and refetch
    // on mount/focus. Do NOT retry a 404 — the endpoint is simply absent on some
    // deployments, and we fall back to the auth-context data below.
    retry: (failureCount, err: any) => {
      if (err?.response?.status === 404) return false;
      return failureCount < 3;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    throwOnError: false,
  });

  const fallback = useMemo(
    () => fallbackBootstrap(user, organization, project, projects),
    [user, organization, project, projects],
  );

  useEffect(() => {
    const data = bff.data ?? fallback;
    if (data) hydrate(data);
  }, [bff.data, fallback, hydrate]);

  return { ready: !!(bff.data ?? fallback) };
}
